import { useEffect, useState } from "react";
import { collection, doc, FirestoreError, onSnapshot } from "firebase/firestore";
import { useFirestore } from "../../helpers/firebaseContext";

/*
 * Live Firestore reads for the CTC tab, keyed by PATH so they re-subscribe when the target
 * changes (another order opened, customer picked in a form, order loaded after mount).
 *
 * The portal-wide useFirestoreDoc/useFirestoreCollection subscribe once on mount and never
 * follow a changed reference; they are left as-is for the rest of the portal.
 * Pass null to subscribe to nothing (e.g. while a dependency is still loading).
 */

export interface LiveDoc<T> {
    data: T | undefined;
    exists: boolean;
    loading: boolean;
    error?: FirestoreError;
}

export function useLiveDoc<T>(path: string | null): LiveDoc<T> {
    const db = useFirestore();
    const [state, setState] = useState<LiveDoc<T>>({ data: undefined, exists: false, loading: !!path });

    useEffect(() => {
        if (!path) {
            setState({ data: undefined, exists: false, loading: false });
            return;
        }
        setState((s) => ({ ...s, loading: true }));
        return onSnapshot(doc(db, path), (snap) => {
            setState({ data: snap.exists() ? ({ ...snap.data(), id: snap.id } as T) : undefined, exists: snap.exists(), loading: false });
        }, (error) => {
            console.error("useLiveDoc", path, error);
            setState({ data: undefined, exists: false, loading: false, error });
        });
    }, [db, path]);

    return state;
}

export interface LiveCollection<T> {
    data: T[];
    loading: boolean;
    error?: FirestoreError;
}

/** Whole (small) collection, live. For master data only — big lists use useLivePagedQuery. */
export function useLiveCollection<T>(path: string | null): LiveCollection<T> {
    const db = useFirestore();
    const [state, setState] = useState<LiveCollection<T>>({ data: [], loading: !!path });

    useEffect(() => {
        if (!path) {
            setState({ data: [], loading: false });
            return;
        }
        setState((s) => ({ ...s, loading: true }));
        return onSnapshot(collection(db, path), (snap) => {
            setState({ data: snap.docs.map((d) => ({ ...d.data(), id: d.id }) as T), loading: false });
        }, (error) => {
            console.error("useLiveCollection", path, error);
            setState({ data: [], loading: false, error });
        });
    }, [db, path]);

    return state;
}
